import {DataTableColumn, DataTableColumnGroup} from "mantine-datatable";
import {TransportMethod, StatisticsGroupWrapper, StatisticsType, TransportCategory} from "@ots-frontend/ots-data";
import {MouseEvent, useCallback, useMemo} from "react";
import {ActionIcon, createStyles, Group} from "@mantine/core";
import {IconChevronRight, IconFileExport} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {getColumnFilterProps, UseDataTableFilterProps} from "../DataTable/useDataTableFilter";
import {EntityIcon} from "../../icon/EntityIcon/EntityIcon";
import {TFunction} from "i18next";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {EMPTY_STATS_ARRAY} from "./StatisticsDataTable";
import {StatisticsExportLink} from "../../exportable/StatisticsExportLink/StatisticsExportLink";

export type StatisticsDataGroup = {
  id: string
  name: string
}

export const toStatisticsDataGroups = (maps: Record<string, StatisticsGroupWrapper>[]): StatisticsDataGroup[] => {
  const groupMap: Map<string, StatisticsDataGroup> = new Map<string, StatisticsDataGroup>()

  for (const map of maps) {
    for (const key of Object.keys(map)) {
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          id: key,
          name: map[key].name
        })
      }
    }
  }

  return [...groupMap.values()]
}

export type UseStatisticsDataTableColumnsProps =
  Partial<Omit<UseDataTableFilterProps<StatisticsGroupWrapper>, 'records'>>
  & {
  nestLevel?: number
  expandedIds?: string[]
  rateModel?: string
  category?: TransportCategory
  method?: TransportMethod
  types: StatisticsType[]
  grouping?: {
    groupRetriever: (stats: StatisticsGroupWrapper) => Record<string, StatisticsGroupWrapper>
    groups: StatisticsDataGroup[]
  }
  records?: StatisticsGroupWrapper[],
  showTotals?: boolean
  onExportClick?: (e: MouseEvent) => void
}

export type UseStatisticsDataTableColumnsResult = {
  columns?: DataTableColumn<StatisticsGroupWrapper>[]
  groups?: DataTableColumnGroup<StatisticsGroupWrapper>[]
}

const useStyles = createStyles((theme) => ({
  expandIcon: {
    transition: 'transform 0.2s ease',
  },
  expandIconRotated: {
    transform: 'rotate(90deg)',
  },
}));

export const getStatisticsColumns = (types: StatisticsType[], method: TransportMethod | undefined, rateModel: string | undefined, category: TransportCategory | undefined, t: TFunction, records: StatisticsGroupWrapper[], showTotals: boolean, groupingId: string | undefined = undefined, groupRetriever: ((stats: StatisticsGroupWrapper) => Record<string, StatisticsGroupWrapper>) | undefined = undefined): DataTableColumn<StatisticsGroupWrapper>[] => {
  return types.map((type, index) => ({
    title: t(type.displayValue.id, type.displayValue.defaultMessage),
    accessor: "value" + (groupingId ? "_" + groupingId + "_" : "") + index,
    width: 100,
    sortable: true,
    render: (data) => {
      const stats = groupingId && groupRetriever ? groupRetriever(data)[groupingId] : data

      return (
        <FormattedQuantity
          value={{
            number: stats?.getValue(method, type, rateModel, category) ?? 0,
          }}
          options={type.unitType === 'currency' ? {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          } : undefined}
        />
      )
    },
    footer: showTotals ? (
      <DataTableSumFooter
        value={{
          number: records.reduce((prev, curr) => {
            const stats = groupingId && groupRetriever ? groupRetriever(curr)[groupingId] : curr
            const value = stats?.getValue(method, type, rateModel, category) ?? 0
            return prev + value
          }, 0)
        }}
      />
    ) : undefined,
  }))
}

export const useStatisticsDataTableColumns = (props: UseStatisticsDataTableColumnsProps): UseStatisticsDataTableColumnsResult => {
  const {
    filterConfig,
    nestLevel,
    expandedIds,
    rateModel,
    category,
    method,
    types,
    grouping,
    onExportClick,
    showTotals = false,
    records = EMPTY_STATS_ARRAY,
  } = props

  const {t} = useTranslation(["common"])
  const {cx, classes} = useStyles();

  const _onExportClick = useCallback((e: MouseEvent) => {
    if (onExportClick) {
      onExportClick(e)
    }
  }, [onExportClick])

  const columns: DataTableColumn<StatisticsGroupWrapper>[] | undefined = useMemo(() => {
    const dataTableColumns: DataTableColumn<StatisticsGroupWrapper>[] = [{
      ...getColumnFilterProps(filterConfig, "name"),
      title: (
        <Group spacing="xs" position="left">
          <StatisticsExportLink
            rateModel={rateModel}
            category={category}
            method={method}
            types={types}
            data={records}
            onClick={(event, done) => {
              done()
            }}>
            <ActionIcon variant="transparent">
              <IconFileExport size={14}/>
            </ActionIcon>
          </StatisticsExportLink>
          {t("common.entity.statistics.table.column.name", "Name")}
        </Group>
      ),
      accessor: "name",
      width: 100,
      sortable: true,
      render: ({id, name, entityType, childMap}) => (
        <Group spacing="xs" ml={nestLevel ? (nestLevel * 30) : undefined}>
          {expandedIds &&
            <IconChevronRight
              size="0.9em"
              className={cx(classes.expandIcon, {
                [classes.expandIconRotated]: expandedIds.includes(id),
              })}
            />
          }
          {entityType &&
            <EntityIcon
              entityType={entityType}
              size="0.9em"
            />
          }
          <EntityAnchor
            value={{
              objectId: id,
              objectName: name,
              objectType: entityType
            }}
          />
        </Group>
      ),
    }]

    dataTableColumns.push(...getStatisticsColumns(types, method, rateModel, category, t, records, showTotals))

    return dataTableColumns
  }, [
    t,
    filterConfig,
    cx,
    classes.expandIcon,
    classes.expandIconRotated,
    nestLevel,
    expandedIds,
    types,
    method,
    rateModel,
    category,
    records,
    showTotals,
    _onExportClick
  ])

  const groups: DataTableColumnGroup<StatisticsGroupWrapper>[] | undefined = useMemo(() => {
    if (grouping) {
      const dataTableGroups: DataTableColumnGroup<StatisticsGroupWrapper>[] = [{
        id: '',
        columns: [{
          ...getColumnFilterProps(filterConfig, "name"),
          title: t("common.entity.statistics.table.column.name", "Name"),
          accessor: "name",
          width: 100,
          sortable: true,
          render: ({id, name, entityType, childMap}) => (
            <Group spacing="xs" ml={nestLevel ? (nestLevel * 30) : undefined}>
              {expandedIds &&
                <IconChevronRight
                  size="0.9em"
                  className={cx(classes.expandIcon, {
                    [classes.expandIconRotated]: expandedIds.includes(id),
                  })}
                />
              }
              {entityType &&
                <EntityIcon
                  entityType={entityType}
                  size="0.9em"
                />
              }
              <EntityAnchor
                value={{
                  objectId: id,
                  objectName: name,
                  objectType: entityType
                }}
              />
            </Group>
          ),
        }]
      }]

      for (const group of grouping.groups) {
        dataTableGroups.push({
          id: group.id,
          title: <i>{group.name}</i>,
          columns: getStatisticsColumns(types, method, rateModel, category, t, records, showTotals, group.name, grouping.groupRetriever)
        })
      }

      return dataTableGroups
    }

    return undefined
  }, [
    category,
    classes.expandIcon,
    classes.expandIconRotated,
    cx,
    expandedIds,
    filterConfig,
    grouping,
    method,
    nestLevel,
    rateModel,
    t,
    types,
    records,
    showTotals,
  ])

  return {
    columns: columns,
    groups: groups,
  }
}
